/*
 * Landing page buy box (lp-hero section)
 * ------------------------------------------------------------
 * Self-contained variant / selling-plan / price sync for a page-template
 * product form. Deliberately does NOT reuse the theme's variant-radios
 * element: that one re-renders the section via ?section_id= against the
 * product URL, which only works inside product templates.
 *
 * Add-to-cart itself is handled by the theme's <product-form> element
 * (product-form.js), so cart behavior matches the PDP exactly.
 */
(function () {
  if (customElements.get('lp-buybox')) return;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';

    function toFixed(number, precision) {
      var p = Math.pow(10, precision);
      return (Math.round(number * p) / p).toFixed(precision);
    }

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      var fixed = toFixed(number / 100.0, precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value;
    switch (key) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  class LpBuybox extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-lp-section]') || this;
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      if (!this.form) return;

      var dataEl = this.querySelector('[data-lp-variants]');
      try {
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.subDiscount = parseFloat(this.dataset.subDiscount) || 0;
      this.strings = {
        addToCart: this.dataset.addToCart || 'Add to Cart',
        soldOut: this.dataset.soldOut || 'Sold Out',
        unavailable: this.dataset.unavailable || 'Unavailable',
        pricePrefix: this.dataset.pricePrefix || ' — '
      };

      this.idInput = this.form.querySelector('input[name="id"]');
      this.planInput = this.form.querySelector('input[name="selling_plan"]');
      this.optionGroups = Array.prototype.slice.call(this.querySelectorAll('[data-lp-option-group]'));
      this.planRadios = Array.prototype.slice.call(this.querySelectorAll('[data-lp-plan]'));
      this.frequencySelect = this.querySelector('[data-lp-frequency]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.btnText = this.querySelector('[data-lp-btn-text]');
      this.btnPrice = this.querySelector('[data-lp-btn-price]');
      this.image = this.root.querySelector('[data-lp-image]');
      this.sticky = this.root.querySelector('[data-lp-sticky]');

      var self = this;
      this.addEventListener('change', function (evt) {
        var t = evt.target;
        if (t.matches('[data-lp-option], [data-lp-plan], [data-lp-frequency]')) {
          self.update();
        }
      });

      this.update();
      this.initSticky();
    }

    selectedOptions() {
      return this.optionGroups.map(function (group) {
        var checked = group.querySelector('[data-lp-option]:checked');
        return checked ? checked.value : null;
      });
    }

    currentVariant(options) {
      return this.variants.find(function (variant) {
        var vOptions = variant.options || [variant.option1, variant.option2, variant.option3];
        return options.every(function (value, index) {
          return value === null || vOptions[index] === value;
        });
      }) || null;
    }

    selectedPlanId() {
      var checked = this.planRadios.find(function (r) { return r.checked; });
      if (!checked || checked.value !== 'subscribe') return null;
      if (this.frequencySelect && this.frequencySelect.value) return this.frequencySelect.value;
      return checked.dataset.lpDefaultPlan || null;
    }

    // Ordergroove's selling plans carry no price adjustment, so the plan price equals the one-time price.
    // In that case show the section's Subscribe & Save % off. Ordergroove takes it off in the cart, where the
    // discount is rounded down to the cent ($49.99 - 10% = $45.00), so round the same way.
    subscriptionPrice(variant, alloc) {
      if (alloc && alloc.price < variant.price) return alloc.price;
      if (this.subDiscount > 0) return variant.price - Math.floor(variant.price * this.subDiscount / 100);
      return alloc ? alloc.price : variant.price;
    }

    allocationFor(variant, planId) {
      if (!variant || !planId || !variant.selling_plan_allocations) return null;
      return variant.selling_plan_allocations.find(function (a) {
        return String(a.selling_plan_id) === String(planId);
      }) || null;
    }

    update() {
      var options = this.selectedOptions();
      var variant = this.currentVariant(options);
      var planId = this.selectedPlanId();
      var money = this.moneyFormat;

      // Mark unavailable option values for the current selection (single-option products only)
      if (this.optionGroups.length === 1) {
        var group = this.optionGroups[0];
        var inputs = group.querySelectorAll('[data-lp-option]');
        for (var i = 0; i < inputs.length; i++) {
          var v = this.variants.find(function (variant) {
            var vo = variant.options || [variant.option1];
            return vo[0] === inputs[i].value;
          });
          inputs[i].disabled = !v;
          inputs[i].setAttribute('aria-disabled', v && v.available ? 'false' : 'true');
        }
      }

      // Hidden inputs
      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
      }
      if (this.planInput) {
        if (planId && variant) {
          this.planInput.value = planId;
          this.planInput.disabled = false;
        } else {
          this.planInput.value = '';
          this.planInput.disabled = true;
        }
      }

      // Prices for each plan card (for the current variant)
      var self = this;
      this.querySelectorAll('[data-lp-plan-price]').forEach(function (el) {
        var kind = el.dataset.lpPlanPrice;
        if (!variant) { el.textContent = ''; return; }
        if (kind === 'onetime') {
          el.innerHTML = self.priceMarkup(variant.price, variant.compare_at_price, money);
        } else {
          var pid = self.frequencySelect && self.frequencySelect.value ? self.frequencySelect.value : (el.dataset.lpDefaultPlan || planId);
          var alloc = self.allocationFor(variant, pid);
          var subPrice = self.subscriptionPrice(variant, alloc);
          if (alloc || subPrice < variant.price) {
            var compare = alloc && alloc.compare_at_price && alloc.compare_at_price > subPrice ? alloc.compare_at_price : (variant.price > subPrice ? variant.price : null);
            el.innerHTML = self.priceMarkup(subPrice, compare, money);
          } else {
            el.innerHTML = self.priceMarkup(variant.price, variant.compare_at_price, money);
          }
        }
      });

      // Auto "Save X%" badge
      this.querySelectorAll('[data-lp-save-auto]').forEach(function (el) {
        if (!variant) return;
        var pid = self.frequencySelect && self.frequencySelect.value ? self.frequencySelect.value : (el.dataset.lpDefaultPlan || planId);
        var subPrice = self.subscriptionPrice(variant, self.allocationFor(variant, pid));
        if (subPrice < variant.price) {
          var pct = Math.round((1 - subPrice / variant.price) * 100);
          el.textContent = el.dataset.lpSaveAuto.replace('%s', pct);
          el.hidden = false;
        } else if (el.dataset.lpSaveFallback) {
          el.textContent = el.dataset.lpSaveFallback;
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // Effective price
      var effective = null;
      var available = false;
      if (variant) {
        effective = planId ? this.subscriptionPrice(variant, this.allocationFor(variant, planId)) : variant.price;
        available = !!variant.available;
      }

      // Button
      if (this.submitButton) {
        if (!variant) {
          this.submitButton.setAttribute('disabled', '');
          if (this.btnText) this.btnText.textContent = this.strings.unavailable;
          if (this.btnPrice) this.btnPrice.textContent = '';
        } else if (!available) {
          this.submitButton.setAttribute('disabled', '');
          if (this.btnText) this.btnText.textContent = this.strings.soldOut;
          if (this.btnPrice) this.btnPrice.textContent = '';
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.btnText) this.btnText.textContent = this.strings.addToCart;
          if (this.btnPrice) this.btnPrice.textContent = this.btnPrice.dataset.lpHidePrice === 'true' ? '' : this.strings.pricePrefix + formatMoney(effective, money);
        }
      }

      // Generic price targets
      this.root.querySelectorAll('[data-lp-price]').forEach(function (el) {
        el.textContent = effective !== null ? formatMoney(effective, money) : '';
      });

      // Image swap
      if (this.image && variant && variant.featured_image && variant.featured_image.src) {
        var src = variant.featured_image.src;
        if (this.image.getAttribute('src') !== src) {
          this.image.removeAttribute('srcset');
          this.image.setAttribute('src', src);
          if (variant.featured_image.alt) this.image.setAttribute('alt', variant.featured_image.alt);
        }
      }

      // Sticky bar
      if (this.sticky) {
        var stickyVariant = this.sticky.querySelector('[data-lp-sticky-variant]');
        var stickyBtn = this.sticky.querySelector('[data-lp-sticky-btn]');
        if (stickyVariant) {
          stickyVariant.textContent = options.filter(Boolean).join(' · ');
        }
        if (stickyBtn) {
          if (variant && available) {
            stickyBtn.removeAttribute('disabled');
            stickyBtn.textContent = this.strings.addToCart;
          } else {
            stickyBtn.setAttribute('disabled', '');
            stickyBtn.textContent = variant ? this.strings.soldOut : this.strings.unavailable;
          }
        }
      }

      this.root.dispatchEvent(new CustomEvent('lp:variant', {
        bubbles: true,
        detail: { variant: variant, sellingPlanId: planId, price: effective }
      }));
    }

    priceMarkup(price, compare, money) {
      var html = '';
      if (compare && compare > price) {
        html += '<s>' + formatMoney(compare, money) + '</s>';
      }
      html += formatMoney(price, money);
      return html;
    }

    initSticky() {
      if (!this.sticky) return;
      var self = this;
      var footer = document.querySelector('[data-lp-footer], .trellis-footer, footer');
      var ticking = false;

      function check() {
        ticking = false;
        var rect = self.getBoundingClientRect();
        var pastBuybox = rect.bottom < 0;
        var nearFooter = false;
        if (footer) {
          var fr = footer.getBoundingClientRect();
          nearFooter = fr.top < window.innerHeight - 40;
        }
        var show = pastBuybox && !nearFooter;
        self.sticky.classList.toggle('is-visible', show);
        document.body.classList.toggle('lp-body--sticky-visible', show);
      }

      window.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(check);
        }
      }, { passive: true });
      window.addEventListener('resize', check);
      check();
    }
  }

  customElements.define('lp-buybox', LpBuybox);
})();
